import { Button, Checkbox, Modal, PinInput, Tooltip } from '@mantine/core'
import { useEffect, useState } from 'react'
import { USPhoneInput } from '@/components/ui/USPhoneInput'
import { useMedplum } from '@medplum/react'
import { Patient, ProjectMembership } from '@medplum/fhirtypes'
import { IconDeviceMobile, IconInfoCircle, IconMail } from '@tabler/icons-react'
import { showNotification } from '@mantine/notifications'
import {
  AUTH_CLASSES,
  AUTH_COLORS,
  AUTH_LOGIN_CHECKBOX_STYLES,
  AUTH_LOGIN_PRIMARY_BUTTON_STYLES,
  AUTH_LOGIN_SECONDARY_BUTTON_STYLES,
  AUTH_LOGIN_TEXT_INPUT_STYLES,
} from '../authTheme'

type Profile = {
  key?: string
  value?: string
}

type Props = {
  login?: string
  onFinish: (patient: Patient) => void
  onCancel: () => void
  onProfileSelect: (profiles: Profile[]) => void
}

const defaultAvailableMethods = { phone: false, email: false }

const timerValue = 45

const regExp = /^\+?[1-9]\d{7,14}$/

export const TwoFAModal = ({ login, onFinish, onCancel, onProfileSelect }: Props) => {
  const medplum = useMedplum()

  const [availableMethods, setAvailableMethods] = useState<typeof defaultAvailableMethods>({
    ...defaultAvailableMethods,
  })
  const [newPhone, setNewPhone] = useState<string | undefined>(undefined)
  const [code, setCode] = useState<string | undefined>(undefined)
  const [isCodeSent, setIsCodeSent] = useState<boolean>(false)
  const [timer, setTimer] = useState<number>(0)
  const [triggerTimer, setTriggerTimer] = useState<boolean>(false)
  const [isSetup2fa, setIsSetup2fa] = useState<boolean>(false)
  const [rememberDevice, setRememberDevice] = useState<boolean>(false)
  const [consent, setConsent] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const handleCancel = () => {
    setIsSetup2fa(false)
    setIsCodeSent(false)
    setTriggerTimer(false)
    setTimer(0)
    setCode(undefined)
    setError(undefined)
    onCancel()
  }

  useEffect(() => {
    if (triggerTimer) {
      const interval = setInterval(() => {
        setTimer((currentTimer) => {
          if (currentTimer <= 1) {
            clearInterval(interval)
            setTriggerTimer(false)
            return 0
          }

          return currentTimer - 1
        })
      }, 1000)
      return () => clearInterval(interval)
    }
  }, [triggerTimer])

  useEffect(() => {
    if (login) {
      medplum.get(`/auth/2fa/available-methods?login=${login}`).then((response) => {
        setAvailableMethods(response)
        setIsSetup2fa(true)
      })
    }
  }, [login, medplum])

  const onFinishHandler = async (profile: Patient) => {
    if (newPhone && profile) {
      profile = {
        ...profile,
        telecom: [
          ...(profile.telecom || []).filter((t) => t.system !== 'phone'),
          { system: 'phone', value: newPhone },
        ],
      }
    }

    setCode(undefined)
    setAvailableMethods({ ...defaultAvailableMethods })
    setNewPhone(undefined)
    setIsCodeSent(false)
    setIsSetup2fa(false)

    onFinish(profile)
  }

  const verifyCode = async (initCode?: string) => {
    if (!code && !initCode) {
      showNotification({
        color: 'red',
        message: 'Code has not been provided',
      })
      return
    }

    try {
      const result = await medplum.post('auth/2fa/verify', {
        login,
        code: code || initCode,
        rememberDevice,
      })
      if (result.code) {
        const profile = await medplum.processCode(result.code)
        await onFinishHandler(profile as Patient)
      } else if (result.memberships) {
        onProfileSelect(
          result.memberships.map((item: ProjectMembership) => ({
            key: item.id,
            value: item.project.display,
          })),
        )
        setIsSetup2fa(false)
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      showNotification({
        color: 'red',
        message: 'Invalide code',
      })
    }
  }

  const sendCode = async (sendTo: 'email' | 'phone' = 'phone') => {
    if (newPhone && !newPhone.match(regExp)) {
      showNotification({
        color: 'red',
        message: 'Invalid phone number',
      })
      return
    }
    setTimer(timerValue)
    const result = await medplum.post(`/auth/2fa/send-code`, {
      login,
      phoneNumber: newPhone,
      sendTo,
    })

    if (result?.code) {
      await verifyCode(result.code)
    } else {
      setTriggerTimer(true)
      setIsCodeSent(true)
    }
  }

  return (
    <Modal
      title={
        <div className="absolute text-[20px]">
          {!availableMethods.phone && !isCodeSent && 'Verify Your Identity'}
          {availableMethods.phone && !isCodeSent && 'Device Verification'}
          {isCodeSent && 'Enter your code'}
        </div>
      }
      opened={isSetup2fa}
      onClose={handleCancel}
      classNames={{
        content: `${AUTH_CLASSES.fontScope} !rounded-xl px-2 pb-2`,
      }}
    >
      {!availableMethods.phone && !isCodeSent && (
        <>
          <p className={`${AUTH_CLASSES.mutedText} mb-4 text-[14px]`}>
            Enter your phone number for a verification code.
          </p>
          <p className={`${AUTH_CLASSES.fieldLabel} mb-[7px]`}>Phone number</p>
          <USPhoneInput
            placeholder="Enter phone number"
            value={newPhone}
            disabled={false}
            onChange={(phone) => {
              setNewPhone(phone)
              if (!phone?.match(regExp)) {
                setError('Invalid phone number')
              } else {
                setError(undefined)
              }
            }}
            classNames={{ input: AUTH_CLASSES.inputFocus }}
            styles={{
              input: {
                ...AUTH_LOGIN_TEXT_INPUT_STYLES.input,
                ...(error ? { borderColor: AUTH_COLORS.error } : {}),
              },
            }}
          />
          {error && <p className={`${AUTH_CLASSES.errorText} mt-[6px] text-[12px]`}>{error}</p>}

          <div className="mt-4 flex items-start gap-3">
            <Checkbox
              className="pt-1"
              styles={AUTH_LOGIN_CHECKBOX_STYLES}
              defaultChecked={consent}
              onChange={(e) => setConsent(e.currentTarget.checked)}
            />
            <span className={`${AUTH_CLASSES.secondaryText} text-[14px]`}>
              I consent to receive login verification codes via SMS from BeHuman. Message and data
              rates may apply.
            </span>
          </div>
          <div className="mt-4 flex justify-center">
            <Button
              fullWidth
              className={AUTH_CLASSES.primaryButtonState}
              styles={AUTH_LOGIN_PRIMARY_BUTTON_STYLES}
              onClick={() => sendCode()}
              disabled={!consent || timer > 0}
            >
              {timer > 0 ? `Wait ${timer}s` : 'Send code'}
            </Button>
          </div>
        </>
      )}
      {availableMethods.phone && !isCodeSent && (
        <>
          <p className={`${AUTH_CLASSES.mutedText} mb-4 text-[14px]`}>
            For your security, please choose how you'd like to receive your verification code. This
            extra step helps keep your account safe.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Button
              fullWidth
              className={AUTH_CLASSES.primaryButtonState}
              styles={AUTH_LOGIN_PRIMARY_BUTTON_STYLES}
              disabled={timer > 0}
              onClick={() => sendCode()}
            >
              Send verification code to phone {timer > 0 ? `Wait ${timer}s` : ''}{' '}
              <IconDeviceMobile className="ml-2" />
            </Button>
            <div className={`${AUTH_CLASSES.mutedText} text-center text-[12.5px]`}>
              Message and data rates may apply
            </div>
            <div className="relative my-3 flex h-[1px] w-full justify-center bg-[rgba(0,0,0,0.08)]">
              <div className="absolute top-[-9px] w-[30px] bg-white text-center text-[11.5px] font-medium text-[#ccc]">
                or
              </div>
            </div>
            <Button
              fullWidth
              className={AUTH_CLASSES.secondaryButtonState}
              styles={AUTH_LOGIN_SECONDARY_BUTTON_STYLES}
              disabled={timer > 0}
              onClick={() => sendCode('email')}
            >
              Send verification code to email {timer > 0 ? `Wait ${timer}s` : ''}
              <IconMail className="ml-3" />
            </Button>
          </div>
        </>
      )}
      {isCodeSent && (
        <>
          <p className={`${AUTH_CLASSES.mutedText} mb-4 text-[14px]`}>Enter your 6-digit code</p>
          <span className={`${AUTH_CLASSES.fieldLabel} mb-[7px] block`}>Verification Code:</span>
          <PinInput
            type={/^[0-9]*$/}
            classNames={{ input: AUTH_CLASSES.inputFocus }}
            styles={{
              root: {
                display: 'flex',
                justifyContent: 'space-between',
                gap: 8,
              },
              input: {
                borderRadius: 12,
                borderWidth: 1.5,
                borderColor: AUTH_COLORS.inputBorder,
                fontSize: 15,
              },
            }}
            autoFocus
            placeholder=""
            length={6}
            inputMode="tel"
            value={code}
            onChange={setCode}
            size="lg"
          />
          <div className="mt-4 flex items-start gap-3">
            <Checkbox
              className="pt-1"
              styles={AUTH_LOGIN_CHECKBOX_STYLES}
              onChange={(e) => setRememberDevice(e.currentTarget.checked)}
            />
            <div className="flex flex-1 items-center justify-between">
              <p className="leading-5">
                <span className={`${AUTH_CLASSES.secondaryText} text-[14px]`}>
                  Trust this device for 30 days
                </span>
                <br />
                <span className={`${AUTH_CLASSES.mutedText} text-[14px]`}>
                  (Not recommended for public devices)
                </span>
              </p>
              <Tooltip
                label={
                  <p className="max-w-[290px] text-[13px] whitespace-pre-wrap">
                    Trusting the device means you will not have to authenticate the device each time
                    you login to system. For your protection you will be asked to verify and trust
                    the device every 30 days.
                  </p>
                }
              >
                <Button variant="transparent" className={`${AUTH_CLASSES.softTextClass} !p-0`}>
                  <IconInfoCircle />
                </Button>
              </Tooltip>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <Button
              className={AUTH_CLASSES.secondaryButtonState}
              styles={AUTH_LOGIN_SECONDARY_BUTTON_STYLES}
              disabled={timer > 0}
              onClick={() => {
                setCode(undefined)
                setIsCodeSent(false)
              }}
            >
              {timer > 0 ? `Wait ${timer}s` : 'Back'}
            </Button>
            <Button
              className={AUTH_CLASSES.primaryButtonState}
              styles={AUTH_LOGIN_PRIMARY_BUTTON_STYLES}
              onClick={() => verifyCode()}
            >
              Confirm
            </Button>
          </div>
        </>
      )}
    </Modal>
  )
}
